import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, UploadCloud, Trash2, Plus, ImagePlus, Save, GripVertical } from "lucide-react";
import { Reorder } from "framer-motion";
import { adminApi, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

// Downscale a file to <=maxPx wide/tall and return a JPEG data URL (same approach as Flower Images).
function fileToDataUrl(file, maxPx = 1400, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxPx || height > maxPx) {
        const scale = maxPx / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image")); };
    img.src = url;
  });
}

export function ImageDrop({ value, onChange, testid, aspect = "aspect-video" }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) return toast.error("Please choose an image file");
    setBusy(true);
    try {
      onChange(await fileToDataUrl(file));
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div
        data-testid={`${testid}-dropzone`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); handleFile(e.dataTransfer.files?.[0]); }}
        className={`group relative flex ${aspect} w-full cursor-pointer items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed transition ${over ? "border-[#28282E] bg-[#FBEEF4]" : "border-slate-300 bg-slate-50 hover:border-[#EBBAD1]"}`}
      >
        {value ? (
          <>
            <img src={value} alt="cover" className="h-full w-full object-cover" />
            <div className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition group-hover:opacity-100">
              <span className="flex items-center gap-2 text-sm font-semibold text-white"><UploadCloud className="h-4 w-4" /> Replace image</span>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-1 px-4 text-center text-[#28282E]/55">
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImagePlus className="h-7 w-7" />}
            <span className="text-sm font-semibold text-[#28282E]/70">Drop an image or click to upload</span>
            <span className="text-xs">PNG or JPG — auto-resized</span>
          </div>
        )}
        {busy && value && <div className="absolute inset-0 grid place-items-center bg-black/40"><Loader2 className="h-6 w-6 animate-spin text-white" /></div>}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden"
        data-testid={`${testid}-file-input`}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleFile(f); }} />
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://… (or upload above)"
        data-testid={`${testid}-url`} className="mt-2 text-xs" />
    </div>
  );
}

const LABELS = {
  new_order: "New Order",
  friday_specials: "Friday Specials",
  events: "Events",
  merchandise_ideas: "Merchandise Ideas",
  flower_care: "Flower Care",
};

function DeckCard({ deck, onSaved }) {
  const [draft, setDraft] = useState(deck);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(deck); }, [deck]);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const sections = draft.sections || [];
  const setSections = (next) => set({ sections: next });
  const updateSection = (i, patch) => setSections(sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const addSection = () => setSections([...sections, { id: "", title: "", body: "", image_url: "" }]);
  const removeSection = (i) => setSections(sections.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const saved = await adminApi.updateDeck(draft.key, {
        title: draft.title,
        subtitle: draft.subtitle || "",
        body: draft.body || "",
        image_url: draft.image_url || "",
        sections: sections.map((s) => ({ id: s.id || undefined, title: s.title || "", body: s.body || "", image_url: s.image_url || "" })),
        is_visible: !!draft.is_visible,
      });
      onSaved(saved);
      toast.success(`${LABELS[draft.key] || draft.title} saved`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div data-testid={`deck-card-${draft.key}`} className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-5 py-3">
        <span className="text-xs font-bold uppercase tracking-widest text-[#D46117]">{LABELS[draft.key] || draft.key}</span>
      </div>
      <div className="grid gap-6 p-5 lg:grid-cols-2">
        {/* Left: live preview + cover image */}
        <div className="space-y-4">
          <div className="relative overflow-hidden rounded-2xl">
            {draft.image_url
              ? <img src={draft.image_url} alt={draft.title} className="h-52 w-full object-cover" />
              : <div className="h-52 w-full bg-slate-200" />}
            <div className="absolute inset-0 bg-gradient-to-t from-[#28282E]/90 via-[#28282E]/45 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-end p-4 text-white">
              <div className="text-[10px] font-bold uppercase tracking-wide text-[#EBBAD1]">{draft.subtitle || "\u00a0"}</div>
              <div className="font-serif text-2xl font-extrabold leading-tight">{draft.title || "Untitled"}</div>
              <p className="mt-1 line-clamp-2 text-xs text-white/90">{draft.body}</p>
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs font-semibold text-[#28282E]/70">Cover / tile image</Label>
            <ImageDrop value={draft.image_url} onChange={(v) => set({ image_url: v })} testid={`deck-${draft.key}-cover`} />
          </div>
        </div>

        {/* Right: text fields */}
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block text-xs font-semibold text-[#28282E]/70">Title</Label>
            <Input value={draft.title || ""} onChange={(e) => set({ title: e.target.value })} data-testid={`deck-${draft.key}-title`} />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs font-semibold text-[#28282E]/70">Subtitle</Label>
            <Input value={draft.subtitle || ""} onChange={(e) => set({ subtitle: e.target.value })} data-testid={`deck-${draft.key}-subtitle`} />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs font-semibold text-[#28282E]/70">Body (short preview on Home)</Label>
            <Textarea rows={4} value={draft.body || ""} onChange={(e) => set({ body: e.target.value })} data-testid={`deck-${draft.key}-body`} />
          </div>
          <label className="flex items-center gap-2.5 rounded-xl bg-[#FBEEF4] px-3 py-2.5">
            <Checkbox checked={!!draft.is_visible} onCheckedChange={(v) => set({ is_visible: !!v })} data-testid={`deck-${draft.key}-visible`} />
            <span className="text-sm font-semibold text-[#28282E]">Show on Home</span>
          </label>
        </div>
      </div>

      {/* Detail sections */}
      <div className="border-t border-slate-100 px-5 py-5">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-bold text-[#28282E]">Detail sections ({sections.length})</span>
          <Button variant="outline" size="sm" onClick={addSection} data-testid={`deck-${draft.key}-add-section`}
            className="rounded-full border-[#EBBAD1] text-[#28282E] hover:bg-[#FBEEF4]">
            <Plus className="mr-1 h-4 w-4" /> Add section
          </Button>
        </div>
        <div className="space-y-4">
          {sections.map((s, i) => (
            <div key={i} data-testid={`deck-${draft.key}-section-${i}`} className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-[1fr_220px]">
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <Input value={s.title || ""} onChange={(e) => updateSection(i, { title: e.target.value })} placeholder="Section title"
                    data-testid={`deck-${draft.key}-section-${i}-title`} />
                  <button onClick={() => removeSection(i)} data-testid={`deck-${draft.key}-section-${i}-delete`}
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[#28282E]/55 hover:bg-red-50 hover:text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <Textarea rows={3} value={s.body || ""} onChange={(e) => updateSection(i, { body: e.target.value })} placeholder="Section body"
                  data-testid={`deck-${draft.key}-section-${i}-body`} />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs font-semibold text-[#28282E]/70">Image (optional)</Label>
                <ImageDrop value={s.image_url} onChange={(v) => updateSection(i, { image_url: v })} testid={`deck-${draft.key}-section-${i}-image`} aspect="aspect-square" />
              </div>
            </div>
          ))}
          {sections.length === 0 && <p className="text-sm text-[#28282E]/55">No detail sections yet.</p>}
        </div>
      </div>

      <div className="flex justify-end border-t border-slate-100 px-5 py-4">
        <Button onClick={save} disabled={saving} data-testid={`deck-${draft.key}-save`}
          className="rounded-full bg-[#28282E] px-6 text-white hover:bg-[#1F1F24]">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save changes
        </Button>
      </div>
    </div>
  );
}

function DeckOrderBar({ decks, onReorder, onPersist }) {
  return (
    <div className="mb-6 rounded-3xl border border-slate-200 bg-white p-4" data-testid="deck-order-bar">
      <div className="mb-3 flex items-center gap-2">
        <GripVertical className="h-4 w-4 text-[#D46117]" />
        <span className="text-sm font-bold text-[#28282E]">Tile order</span>
        <span className="text-xs text-[#28282E]/55">— drag to reorder how tiles appear on the store home</span>
      </div>
      <Reorder.Group axis="y" values={decks} onReorder={onReorder} className="space-y-2">
        {decks.map((d) => (
          <Reorder.Item key={d.key} value={d} onDragEnd={onPersist} data-testid={`deck-order-${d.key}`}
            className="flex cursor-grab select-none items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 active:cursor-grabbing">
            <GripVertical className="h-4 w-4 shrink-0 text-[#28282E]/55" />
            {d.image_url ? <img src={d.image_url} alt="" className="h-9 w-14 shrink-0 rounded-md object-cover" /> : <div className="h-9 w-14 shrink-0 rounded-md bg-slate-200" />}
            <span className="text-sm font-semibold text-[#28282E]">{LABELS[d.key] || d.key}</span>
            {!d.is_visible && <span className="ml-auto rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase text-[#28282E]/70">Hidden</span>}
          </Reorder.Item>
        ))}
      </Reorder.Group>
    </div>
  );
}

export default function HomeContent() {
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const orderRef = useRef([]);
  orderRef.current = decks.map((d) => d.key);

  useEffect(() => {
    adminApi.listDecks().then(setDecks).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message)).finally(() => setLoading(false));
  }, []);

  const onSaved = (saved) => setDecks((list) => list.map((d) => (d.key === saved.key ? { ...saved } : d)));
  const persistOrder = async () => {
    try { await adminApi.reorderDecks(orderRef.current); toast.success("Tile order saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="admin-home-content">
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-bold text-[#28282E]">Home Content</h1>
        <p className="text-sm text-[#28282E]/70">Edit the tiles shown on the store home screen — titles, copy, and background/tile images. Drag & drop or upload photos.</p>
      </div>
      {loading ? (
        <div className="grid place-items-center py-20 text-[#28282E]/55"><Loader2 className="h-8 w-8 animate-spin" /></div>
      ) : (
        <>
          <DeckOrderBar decks={decks} onReorder={setDecks} onPersist={persistOrder} />
          <div className="space-y-6">
            {decks.map((d) => <DeckCard key={d.key} deck={d} onSaved={onSaved} />)}
          </div>
        </>
      )}
    </div>
  );
}
